import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Column, ConfirmationModal, EnhancedAutocomplete, Typography } from 'seagull';
import { ContactRolePicker } from '~/components/ContactRolePicker/ContactRolePicker';
import { useClients } from '~/hooks/reactQuery/queries/useClients';
import { useLocaleKeys } from '~/i18n';
import { contactFullName } from '../ContactsDashboardRow.utils';
import { CLIENT_FIELD_NAME, GetAddContactToClientModalTID, ROLE_FIELD_NAME, addContactToClientModalTextsPath, associableClientsFor } from './AddContactToClientModal.utils';
import { useLinkContactToClient } from './hooks/useLinkContactToClient';
export const AddContactToClientModal = (param)=>{
    let { contact, dismissModal, testId } = param;
    const { t } = useLocaleKeys(addContactToClientModalTextsPath);
    const { AddContactToClientModalTestIds } = useMemo(()=>GetAddContactToClientModalTID(testId), [
        testId
    ]);
    const { clients, isLoading, error } = useClients({
        refreshOnHookMount: true
    });
    const { errorMessage, linkContact } = useLinkContactToClient({
        contact,
        dismissModal,
        successToastTestId: AddContactToClientModalTestIds.SuccessToast()
    });
    const { control, handleSubmit, watch, formState: { errors } } = useForm({
        mode: 'all',
        defaultValues: {
            [CLIENT_FIELD_NAME]: '',
            [ROLE_FIELD_NAME]: ''
        }
    });
    const associableClients = useMemo(()=>associableClientsFor(contact, clients), [
        contact,
        clients
    ]);
    const chosenClientId = watch(CLIENT_FIELD_NAME);
    const clientErrorText = error != null ? t.failedToLoadClients() : errors[CLIENT_FIELD_NAME] != null ? t.clientRequiredError() : undefined;
    const clientPickerProps = {
        bi: {
            name: 'add_contact_to_client_picker'
        },
        testId: AddContactToClientModalTestIds.ClientAutocomplete(),
        size: 'medium',
        options: associableClients,
        keyField: 'clientId',
        labelField: 'name',
        isLoading,
        disabled: error != null,
        textFieldProps: {
            bi: {
                name: 'add_contact_to_client_picker'
            },
            label: t.clientLabel(),
            placeholder: t.clientPlaceholder(),
            alwaysShrinkLabel: true
        },
        emptySearchText: t.noMatches(),
        error: clientErrorText != null,
        helperText: clientErrorText
    };
    const renderClientPicker = (param)=>{
        let { field } = param;
        var _associableClients_find;
        return /*#__PURE__*/ _jsx(EnhancedAutocomplete, _object_spread_props(_object_spread({}, clientPickerProps), {
            selectedOption: (_associableClients_find = associableClients.find((client)=>client.clientId === field.value)) !== null && _associableClients_find !== void 0 ? _associableClients_find : null,
            applyOptionChange: (option)=>{
                var _ref;
                return field.onChange((_ref = option === null || option === void 0 ? void 0 : option.clientId) !== null && _ref !== void 0 ? _ref : '');
            }
        }));
    };
    return /*#__PURE__*/ _jsx(ConfirmationModal, {
        openBottomSheetOnMobile: true,
        title: t.title(),
        primaryButtonLabel: t.saveButton(),
        secondaryButtonLabel: t.cancelButton(),
        testId: AddContactToClientModalTestIds.Root(),
        textAlignCenter: false,
        primaryButtonTestId: AddContactToClientModalTestIds.SaveButton(),
        secondaryButtonTestId: AddContactToClientModalTestIds.CancelButton(),
        bi: {
            primaryButton: {
                name: 'add_contact_to_client_submit'
            },
            secondaryButton: {
                name: 'add_contact_to_client_cancel'
            },
            closeButton: {
                name: 'add_contact_to_client_close'
            }
        },
        onPrimaryButtonClick: handleSubmit(linkContact),
        onSecondaryButtonClick: dismissModal,
        onCloseButtonClick: dismissModal,
        content: /*#__PURE__*/ _jsxs(Column, {
            gap: 24,
            children: [
                /*#__PURE__*/ _jsx(Typography, {
                    testId: AddContactToClientModalTestIds.Description(),
                    children: t.description({
                        name: contactFullName(contact)
                    })
                }),
                /*#__PURE__*/ _jsx(Controller, {
                    control: control,
                    name: CLIENT_FIELD_NAME,
                    rules: {
                        required: true
                    },
                    render: renderClientPicker
                }),
                chosenClientId !== '' && /*#__PURE__*/ _jsx(ContactRolePicker, {
                    control: control,
                    name: ROLE_FIELD_NAME,
                    testId: AddContactToClientModalTestIds.RolePicker()
                }),
                errorMessage != null && /*#__PURE__*/ _jsx(Typography, {
                    variant: "paragraph2",
                    skin: "error",
                    testId: AddContactToClientModalTestIds.SaveError(),
                    children: errorMessage
                })
            ]
        })
    });
};
